import React from 'react';
import { Container, Typography, Paper, Snackbar, Alert, CircularProgress, Box, Breadcrumbs, Link as MuiLink } from '@mui/material';
import EventFormContainer, { type EventFormData } from '../../features/event-form-feature/EventFormContainer';
import { mapApiEventToFormData } from '../../features/event-form-feature/mapApiEventToFormData';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { sectorCanCreateEvents } from '../../permissions';
import { eventService } from '../../services/eventService';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { EVENT_FORM_REGISTRATION_STEP } from '../../features/event-form-feature/eventFormShared';

const CreateEventPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const canCreateEvents = sectorCanCreateEvents(user);
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams] = useSearchParams();
  const initialStep = searchParams.get('step') === 'inscricao' ? EVENT_FORM_REGISTRATION_STEP : 0;
  
  
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [draftId, setDraftId] = React.useState<string|undefined>(undefined);
  const [eventData, setEventData] = React.useState<Partial<EventFormData> | undefined>(undefined); // Corrigido o tipo aqui
  const [isEditMode, setIsEditMode] = React.useState(false);

  const resolveErrorMessage = (err: any, fallback: string) => {
    const status = err?.response?.status;
    const serverMessage =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.response?.data?.details;

    if (typeof serverMessage === 'string' && serverMessage.trim().length > 0) {
      return serverMessage;
    }

    // Nginx / proxies geralmente retornam 413 quando ultrapassa o body size
    if (status === 413) {
      return 'A imagem ultrapassou o limite de tamanho permitido pelo servidor (tente comprimir/redimensionar).';
    }

    if (err instanceof Error && err.message) return err.message;
    if (typeof err?.message === 'string' && err.message.trim().length > 0) return err.message;

    return fallback;
  };
  
  // Buscar dados do evento se estiver em modo de edição
  React.useEffect(() => {
  const fetchEventData = async () => {
    if (eventId) {
      try {
        setLoading(true);
        setIsEditMode(true);

        console.log('Buscando dados do evento:', eventId);

        // Buscar dados do evento da API (por ID - funciona mesmo após mudar nome/slug)
        const response = await eventService.getEventWorkspace(eventId);

        console.log('Dados do evento carregados:', response.data);

        setEventData(mapApiEventToFormData(response.data));
      } catch (err) {
        console.error('Erro ao buscar dados do evento:', err);
        setError(resolveErrorMessage(err, 'Erro ao carregar dados do evento.'));
      } finally {
        setLoading(false);
      }
    }
  };

  fetchEventData();
}, [eventId]);

  const reloadEventData = React.useCallback(async () => {
    if (!eventId) return;
    const response = await eventService.getEventWorkspace(eventId);
    if (response?.data) {
      setEventData(mapApiEventToFormData(response.data));
    }
  }, [eventId]);
    
  
  // Handler para submeter o evento (publicar ou atualizar)
  const handleEventSubmit = async (data: EventFormData) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Enviando dados para', isEditMode ? 'atualização' : 'publicação', ':', data);
      
      let response;
      
      if (isEditMode && eventId) {
        // Atualizar evento existente (por ID)
        response = await eventService.updateEvent(eventId, data);
        setSuccess('Evento atualizado com sucesso!');
      } else {
        // Criar novo evento
        response = await eventService.createEvent(data);
        setSuccess('Evento publicado com sucesso!');
      }
      
      console.log('Evento', isEditMode ? 'atualizado' : 'criado', 'com sucesso:', response);
      
      // Redirecionar para a lista de eventos após 2 segundos
      setTimeout(() => {
        navigate('/dashboard/eventos');
      }, 2000);
      
    } catch (err) {
      console.error('Erro ao', isEditMode ? 'atualizar' : 'criar', 'evento:', err);
      setError(resolveErrorMessage(err, `Erro ao ${isEditMode ? 'atualizar' : 'criar'} evento. Tente novamente.`));
    } finally {
      setLoading(false);
    }
  };
  
  // Handler para salvar rascunho
  const handleSaveDraft = async (data: EventFormData) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Salvando rascunho:', data);
      
      let response;
      
      if (isEditMode && eventId) {
        // Salvar como rascunho de evento existente
        //response = await eventService.saveDraftUpdate(eventId, data);
      } else {
        // Salvar como novo rascunho
        response = await eventService.saveDraft(data, draftId);
        setDraftId(response.data._id);
      }
      
      console.log('Rascunho salvo com sucesso:', response);
      
      setSuccess('Rascunho salvo com sucesso!');
      
    } catch (err) {
      console.error('Erro ao salvar rascunho:', err);
      setError(resolveErrorMessage(err, 'Erro ao salvar rascunho. Tente novamente.'));
    } finally {
      setLoading(false);
    }
  };
  
  // Handler para fechar alertas
  const handleCloseAlert = () => {
    setError(null);
    setSuccess(null);
  };

  // Mostrar loading enquanto carrega dados do evento
  if (loading && isEditMode && !eventData) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '400px'
        }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Carregando dados do evento...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!canCreateEvents) {
    return (
      <Container maxWidth="sm" sx={{ mt: 6 }}>
        <Alert severity="info">
          Este acesso só visualiza eventos. A paróquia principal precisa liberar a criação.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      <Breadcrumbs sx={{ mb: 1, fontSize: '0.78rem', '& .MuiBreadcrumbs-separator': { mx: 0.75 } }}>
        <MuiLink
          component={Link}
          to="/dashboard/eventos"
          underline="hover"
          color="text.secondary"
          sx={{ fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', fontSize: '0.78rem' }}
        >
          Eventos
        </MuiLink>
        <Typography sx={{ fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', fontSize: '0.78rem', color: 'text.secondary' }}>
          {isEditMode ? 'Editar evento' : 'Novo evento'}
        </Typography>
      </Breadcrumbs>
      {loading && (
        <Box sx={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          zIndex: 9999
        }}>
          <CircularProgress />
        </Box>
      )}
      
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {isEditMode ? 'Editar Evento' : 'Criar Novo Evento'}
        </Typography>
        
        <EventFormContainer 
          initialData={eventData}
          initialStep={isEditMode ? initialStep : 0}
          onSubmit={handleEventSubmit}
          onSaveDraft={handleSaveDraft}
          onReloadEvent={reloadEventData}
        />
      </Paper>
      
      {/* Alertas de sucesso e erro */}
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      
      <Snackbar 
        open={!!success} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CreateEventPage;
